import type { OpenHouse as RawOpenHouse } from 'data/listing';

/**
 * OpenHouse Value Object
 * 
 * Domain concept: "When can you visit this property?"
 * 
 * Business rules:
 * - Only future open houses are relevant for display
 * - Open house date must exist and be in the future
 * 
 * Computed facts:
 * - hasValue: Whether this is a valid open house with a date
 * - isFuture: Whether the open house is in the future
 * - isToday: Whether the open house is today
 * 
 * Usage:
 * - Format: src/ui/open-house/format-open-house.ts
 * - UI: src/ui/open-house/open-house-card.tsx
 */
export class OpenHouseVO {
  readonly #date: Date | null;
  readonly #from: string | null;
  readonly #to: string | null;
  readonly #raw: RawOpenHouse;

  private constructor(openHouse: RawOpenHouse) {
    this.#raw = openHouse;
    this.#date = openHouse.date ? new Date(openHouse.date) : null;
    this.#from = openHouse.from;
    this.#to = openHouse.to;
  }

  /**
   * Creates an OpenHouseVO from raw open house data.
   */
  static from(openHouse: RawOpenHouse): OpenHouseVO {
    return new OpenHouseVO(openHouse);
  }

  /**
   * Filters an array of open houses to only future ones, sorted chronologically.
   * This is the single source of truth for open house filtering.
   * 
   * Business Rules:
   * - Only show open houses that haven't happened yet
   * - Sort by date ascending (soonest first)
   */
  static filterFuture(openHouses: RawOpenHouse[] | null): OpenHouseVO[] {
    if (!openHouses || openHouses.length === 0) {
      return [];
    }

    return openHouses
      .map(oh => OpenHouseVO.from(oh))
      .filter(oh => oh.isFuture)
      .sort((a, b) => {
        if (!a.date || !b.date) return 0;
        return a.date.getTime() - b.date.getTime();
      });
  }

  /**
   * Filters and sorts open houses, returning raw data.
   * Use this when you need the business logic but consumers expect raw OpenHouse[].
   */
  static filterFutureRaw(openHouses: RawOpenHouse[] | null): RawOpenHouse[] {
    return OpenHouseVO.filterFuture(openHouses).map(vo => vo.raw);
  }

  /**
   * Whether this open house has valid date data.
   */
  get hasValue(): boolean {
    return this.#date !== null;
  }

  /**
   * Whether the open house is in the future.
   */
  get isFuture(): boolean {
    if (!this.#date) return false;
    return new Date() < this.#date;
  }

  /**
   * Whether the open house is today.
   */
  get isToday(): boolean {
    if (!this.#date) return false;
    const today = new Date();
    return (
      this.#date.getDate() === today.getDate() &&
      this.#date.getMonth() === today.getMonth() &&
      this.#date.getFullYear() === today.getFullYear()
    );
  }

  /**
   * The open house date.
   */
  get date(): Date | null {
    return this.#date;
  }

  /**
   * The start time string (e.g., "11:00AM").
   */
  get from(): string | null {
    return this.#from;
  }

  /**
   * The end time string (e.g., "2:00PM").
   */
  get to(): string | null {
    return this.#to;
  }

  /**
   * Returns the raw open house data for compatibility with existing code.
   */
  get raw(): RawOpenHouse {
    return this.#raw;
  }
}
